import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/common/Header';
import api from '../services/api';
import { AlertTriangle, Shield, DollarSign, Clock, Bell, Key, CheckCircle, XCircle, Lock, Activity, Info, HelpCircle, X, Zap, MapPin, Cloud, Droplets, TrendingUp } from 'lucide-react';

export default function Settings() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Safety Settings - CRITICAL FOR ALEX
  const [dryRunMode, setDryRunMode] = useState(true); // DEFAULT: TRUE for safety
  const [automationEnabled, setAutomationEnabled] = useState(false); // DEFAULT: FALSE
  const [maxDailyBudget, setMaxDailyBudget] = useState(500);
  const [maxBidAdjustment, setMaxBidAdjustment] = useState(100);
  const [cooldownHours, setCooldownHours] = useState(2);

  // Google Ads Connection
  const [, setLoadingGoogleStatus] = useState(true);
  const [googleAccounts, setGoogleAccounts] = useState<any[]>([]);

  // Notification Settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [notificationEmail, setNotificationEmail] = useState('alex@networds.co.za');

  // Password Change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Login Activity Tracking
  const [loginHistory, setLoginHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Help Modals
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showHelpMeChoose, setShowHelpMeChoose] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [showComingFeatures, setShowComingFeatures] = useState(false);

  // LocalStorage key for settings persistence
  const SETTINGS_KEY = 'weather_ads_settings';

  // Load settings from API on mount (with localStorage fallback)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Try to load from API first
        const response = await api.get('/settings');
        if (response.data.success && response.data.settings) {
          const settings = response.data.settings;
          setDryRunMode(settings.dry_run_mode ?? true);
          setAutomationEnabled(settings.automation_enabled ?? false);
          setMaxDailyBudget(settings.max_daily_budget ?? 500);
          setMaxBidAdjustment(settings.max_bid_adjustment ?? 100);
          setCooldownHours(settings.cooldown_hours ?? 2);

          // Save to localStorage as cache
          localStorage.setItem(SETTINGS_KEY, JSON.stringify({
            dryRunMode: settings.dry_run_mode,
            automationEnabled: settings.automation_enabled,
            maxDailyBudget: settings.max_daily_budget,
            maxBidAdjustment: settings.max_bid_adjustment,
            cooldownHours: settings.cooldown_hours,
            lastUpdated: new Date().toISOString(),
          }));
        }
      } catch (error) {
        console.error('Failed to load settings from API, trying localStorage:', error);

        // Fallback to localStorage if API fails
        try {
          const savedSettings = localStorage.getItem(SETTINGS_KEY);
          if (savedSettings) {
            const settings = JSON.parse(savedSettings);
            setDryRunMode(settings.dryRunMode ?? true);
            setAutomationEnabled(settings.automationEnabled ?? false);
            setMaxDailyBudget(settings.maxDailyBudget ?? 500);
            setMaxBidAdjustment(settings.maxBidAdjustment ?? 100);
            setCooldownHours(settings.cooldownHours ?? 2);
            setEmailNotifications(settings.emailNotifications ?? true);
            setNotificationEmail(settings.notificationEmail ?? 'alex@networds.co.za');
          }
        } catch (localError) {
          console.error('Failed to load settings from localStorage:', localError);
        }
      }
    };

    if (isAuthenticated) {
      loadSettings();
    }
  }, [isAuthenticated]);

  // Handle scrolling to hash anchor on navigation
  useEffect(() => {
    if (location.hash) {
      // Wait for page to render, then scroll to anchor
      setTimeout(() => {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  // Auto-save critical settings (dryRunMode, automationEnabled) whenever they change
  useEffect(() => {
    const autoSaveSettings = async () => {
      try {
        const currentSettings = localStorage.getItem(SETTINGS_KEY);
        const settings = currentSettings ? JSON.parse(currentSettings) : {};

        // Update only if settings have been loaded (to avoid overwriting on initial mount)
        if (Object.keys(settings).length > 0 && isAuthenticated) {
          // Save to API
          try {
            await api.patch('/settings', {
              dry_run_mode: dryRunMode,
              automation_enabled: automationEnabled,
              max_daily_budget: maxDailyBudget,
              max_bid_adjustment: maxBidAdjustment,
              cooldown_hours: cooldownHours,
            });
          } catch (apiError) {
            console.error('Failed to save settings to API:', apiError);
          }

          // Save to localStorage as cache
          settings.dryRunMode = dryRunMode;
          settings.automationEnabled = automationEnabled;
          settings.maxDailyBudget = maxDailyBudget;
          settings.maxBidAdjustment = maxBidAdjustment;
          settings.cooldownHours = cooldownHours;
          settings.lastUpdated = new Date().toISOString();
          localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));

          // Dispatch custom event to notify other components (e.g., Dashboard) of settings changes
          window.dispatchEvent(new CustomEvent('settingsChanged', {
            detail: { dryRunMode, automationEnabled }
          }));
        }
      } catch (error) {
        console.error('Failed to auto-save critical settings:', error);
      }
    };

    autoSaveSettings();
  }, [dryRunMode, automationEnabled, maxDailyBudget, maxBidAdjustment, cooldownHours, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchLoginHistory = async () => {
      try {
        const response = await api.get('/auth/login-history');
        if (response.data.success) {
          setLoginHistory(response.data.loginHistory);
        }
      } catch (error) {
        console.error('Failed to fetch login history:', error);
      } finally {
        setLoadingHistory(false);
      }
    };

    if (isAuthenticated) {
      fetchLoginHistory();
    }
  }, [isAuthenticated]);

  // Check Google Ads connection status and fetch all accounts
  useEffect(() => {
    const fetchGoogleAccounts = async () => {
      try {
        setLoadingGoogleStatus(true);
        const response = await api.get('/google-accounts');
        if (response.data.success) {
          setGoogleAccounts(response.data.accounts || []);
        }
      } catch (error: any) {
        console.error('Failed to fetch Google accounts:', error);
        setGoogleAccounts([]);
      } finally {
        setLoadingGoogleStatus(false);
      }
    };

    if (isAuthenticated) {
      fetchGoogleAccounts();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  const handleSaveSettings = async () => {
    try {
      // Save settings to localStorage
      const settings = {
        dryRunMode,
        automationEnabled,
        maxDailyBudget,
        maxBidAdjustment,
        cooldownHours,
        emailNotifications,
        notificationEmail,
        lastUpdated: new Date().toISOString(),
      };

      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));

      // Show success message
      alert('✅ Settings saved successfully!\n\n' +
        `Dry Run Mode: ${dryRunMode ? 'ENABLED ✅' : 'DISABLED ⚠️'}\n` +
        `Automation: ${automationEnabled ? 'ENABLED' : 'DISABLED'}\n` +
        `Max Daily Budget: R${maxDailyBudget}\n` +
        `Max Bid Adjustment: ±${maxBidAdjustment}%\n` +
        `Cooldown: ${cooldownHours} hours\n` +
        `Email Notifications: ${emailNotifications ? 'ENABLED' : 'DISABLED'}\n\n` +
        'Settings will persist across page refreshes.'
      );
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('❌ Failed to save settings. Please try again.');
    }
  };

  const handleConnectGoogle = () => {
    // Validate user is loaded
    if (!user || !user.id) {
      alert('❌ Unable to connect. Please refresh the page and try again.');
      return;
    }

    // Redirect to OAuth flow with actual user ID
    window.location.href = `https://weather.team4u.co.za/api/auth/google?userId=${user.id}`;
  };

  const handleDisconnectAccount = async (accountId: number, accountName: string) => {
    const confirmed = window.confirm(
      `⚠️ Disconnect Account?\n\n` +
      `Account: ${accountName}\n\n` +
      `This will remove OAuth tokens and you'll need to reconnect to manage campaigns for this account.\n\n` +
      `Are you sure?`
    );

    if (!confirmed) return;

    try {
      await api.delete(`/google-accounts/${accountId}`);
      alert('✅ Account disconnected successfully!');

      // Refresh accounts list
      const response = await api.get('/google-accounts');
      if (response.data.success) {
        setGoogleAccounts(response.data.accounts || []);
      }
    } catch (error: any) {
      console.error('Failed to disconnect account:', error);
      alert('❌ Failed to disconnect account. Please try again.');
    }
  };

  const handleDryRunToggle = () => {
    if (!dryRunMode) {
      // Switching to DRY RUN (safe)
      setDryRunMode(true);
      alert('✅ Dry Run Mode ENABLED\n\nThe system will NOT make real bid changes.');
    } else {
      // Switching to LIVE (dangerous)
      const confirmed = window.confirm(
        '⚠️ WARNING! ⚠️\n\n' +
        'You are about to DISABLE Dry Run Mode!\n\n' +
        'This means the system will make REAL bid adjustments to your Google Ads campaigns and SPEND MONEY.\n\n' +
        'Have you:\n' +
        '✓ Tested thoroughly in dry run mode for at least 7 days?\n' +
        '✓ Reviewed all execution logs?\n' +
        '✓ Verified your budget caps are correct?\n' +
        '✓ Confirmed email notifications are working?\n\n' +
        'Are you absolutely sure you want to proceed?'
      );
      if (confirmed) {
        const doubleConfirm = window.confirm(
          'FINAL CONFIRMATION\n\n' +
          'Clicking OK will enable LIVE bidding.\n\n' +
          'This is your last chance to cancel!'
        );
        if (doubleConfirm) {
          setDryRunMode(false);
          alert('⚠️ LIVE MODE ACTIVATED\n\nThe system will now make REAL bid adjustments!');
        }
      }
    }
  };

  const handleChangePassword = async () => {
    // Clear previous messages
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Check password strength
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecial) {
      setPasswordError('Password must contain uppercase, lowercase, number, and special character');
      return;
    }

    try {
      // Call API to change password
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });

      setPasswordSuccess('✅ Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Show success message for 5 seconds
      setTimeout(() => {
        setPasswordSuccess('');
      }, 5000);
    } catch (error: any) {
      setPasswordError(error.response?.data?.error || 'Failed to change password. Please check your current password.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Settings</h1>
            <p className="text-gray-400">Configure your automation preferences and safety controls</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowComingFeatures(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 hover:from-purple-500 hover:via-pink-500 hover:to-red-500 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-purple-500/50 animate-pulse"
            >
              <Zap className="w-5 h-5" />
              🚀 Version 2.0 Roadmap
            </button>
            <button
              onClick={() => setShowHowItWorks(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-cyan-500/50"
            >
              <HelpCircle className="w-5 h-5" />
              How It Works
            </button>
          </div>
        </div>

        {/* Recent Login Activity */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-8 h-8 text-cyan-500" />
            <h2 className="text-2xl font-bold text-white">Recent Login Activity</h2>
          </div>

          {loadingHistory ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
              <p className="text-gray-400 mt-2">Loading login history...</p>
            </div>
          ) : loginHistory.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400">No login history available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">Date & Time</th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">IP Address</th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">Location</th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">Browser / Device</th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loginHistory.slice(0, 10).map((entry) => {
                    const isSuccess = entry.action === 'login_success';
                    const timestamp = new Date(entry.timestamp).toLocaleString('en-ZA', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    });

                    // Format location from geo data
                    const location = entry.geo_city && entry.geo_country
                      ? `${entry.geo_city}, ${entry.geo_country}`
                      : entry.geo_country || 'Unknown';

                    return (
                      <tr key={entry.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                        <td className="py-3 px-4 text-white">{timestamp}</td>
                        <td className="py-3 px-4 text-gray-300 font-mono text-sm">{entry.ip_address || 'Unknown'}</td>
                        <td className="py-3 px-4 text-cyan-400 text-sm">{location}</td>
                        <td className="py-3 px-4 text-gray-400 text-sm truncate max-w-xs" title={entry.user_agent}>
                          {entry.user_agent ? entry.user_agent.substring(0, 60) + (entry.user_agent.length > 60 ? '...' : '') : 'Unknown'}
                        </td>
                        <td className="py-3 px-4">
                          {isSuccess ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-900/30 border border-green-500 rounded-full text-green-400 text-sm font-medium">
                              <CheckCircle className="w-4 h-4" />
                              Success
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-900/30 border border-red-500 rounded-full text-red-400 text-sm font-medium">
                              <XCircle className="w-4 h-4" />
                              Failed
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 text-xs text-gray-500">
            <p>Shows the last 10 login attempts. If you see any suspicious activity, contact support immediately.</p>
          </div>
        </div>

        {/* 🚨 CRITICAL SAFETY SECTION 🚨 */}
        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border-2 border-red-500 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-12 h-12 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold text-red-400">⚠️ SAFETY CONTROLS</h2>
                <button
                  onClick={() => setShowHelpMeChoose(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-cyan-400 hover:text-cyan-300 transition-all animate-pulse hover:animate-none"
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Help Me Choose</span>
                </button>
              </div>
              <p className="text-gray-300 mb-4">
                These settings protect your Google Ads budget. <strong>DRY RUN MODE MUST be enabled</strong> until you've thoroughly tested the system.
              </p>

              {/* DRY RUN TOGGLE - MOST IMPORTANT */}
              <div className="bg-gray-900/50 border border-red-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Shield className={`w-8 h-8 ${dryRunMode ? 'text-green-500' : 'text-red-500'}`} />
                    <div>
                      <h3 className="text-xl font-bold text-white">Dry Run Mode</h3>
                      <p className="text-sm text-gray-400">
                        {dryRunMode
                          ? '✅ ENABLED - No real bid changes will be made (SAFE)'
                          : '❌ DISABLED - System will make REAL bid changes (SPENDING MONEY!)'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleDryRunToggle}
                    className={`px-8 py-3 rounded-lg font-bold text-lg transition-all ${
                      dryRunMode
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white'
                        : 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white animate-pulse'
                    }`}
                  >
                    {dryRunMode ? '✅ DRY RUN ACTIVE' : '⚠️ LIVE MODE ACTIVE'}
                  </button>
                </div>
              </div>

              {/* Automation Master Switch */}
              <div className="bg-gray-900/50 border border-orange-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {automationEnabled ? (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    ) : (
                      <XCircle className="w-8 h-8 text-gray-500" />
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-white">Automation Enabled</h3>
                      <p className="text-sm text-gray-400">
                        {automationEnabled
                          ? 'System is actively monitoring weather and executing rules'
                          : 'System is paused - no rules will be executed'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAutomationEnabled(!automationEnabled)}
                    className={`px-8 py-3 rounded-lg font-bold transition-all ${
                      automationEnabled
                        ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white'
                        : 'bg-gray-700 hover:bg-gray-600 text-white'
                    }`}
                  >
                    {automationEnabled ? 'Pause Automation' : 'Enable Automation'}
                  </button>
                </div>

                {/* Info Tooltip: Dry Run vs Automation */}
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-100">
                      <p className="font-semibold mb-1">What's the difference?</p>
                      <p className="text-blue-200/80 mb-2">
                        <span className="font-medium text-blue-100">Enable Automation</span> controls whether the system runs at all (master on/off switch).
                      </p>
                      <p className="text-blue-200/80">
                        <span className="font-medium text-blue-100">Dry Run Mode</span> controls what happens when automation runs -
                        ON = simulate changes only (safe testing), OFF = make real Google Ads bid changes (live mode).
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Protection */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-8 h-8 text-emerald-500" />
            <h2 className="text-2xl font-bold text-white">Budget Protection</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <span>Maximum Daily Budget (R)</span>
                <div className="relative group">
                  <Info className="w-4 h-4 text-cyan-400 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-cyan-700 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 w-64">
                    <p className="whitespace-normal"><strong>Recommended values:</strong><br/>R50-R150/day for testing<br/>R200-R500/day for established campaigns</p>
                  </div>
                </div>
              </label>
              <input
                type="number"
                value={maxDailyBudget}
                onChange={(e) => setMaxDailyBudget(parseInt(e.target.value))}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                System will stop if total daily spend exceeds this amount
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Maximum Bid Adjustment (%)
              </label>
              <input
                type="number"
                value={maxBidAdjustment}
                onChange={(e) => setMaxBidAdjustment(parseInt(e.target.value))}
                min="1"
                max="200"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                Maximum percentage change allowed per adjustment (±)
              </p>
            </div>
          </div>
        </div>

        {/* Timing Controls */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-8 h-8 text-cyan-500" />
            <h2 className="text-2xl font-bold text-white">Timing Controls</h2>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <span>Cooldown Period (hours)</span>
              <div className="relative group">
                <Info className="w-4 h-4 text-cyan-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-cyan-700 rounded-lg text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 w-72">
                  <p className="whitespace-normal"><strong>Global Settings Default:</strong><br/>This cooldown applies to ALL new rules by default. You can override it per rule when creating/editing rules.</p>
                </div>
              </div>
            </label>
            <input
              type="number"
              value={cooldownHours}
              onChange={(e) => setCooldownHours(parseInt(e.target.value))}
              min="1"
              max="24"
              className="w-full md:w-1/2 bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Minimum time between adjustments for the same rule
            </p>
          </div>
        </div>

        {/* Google Ads Connection */}
        <div id="google-accounts" className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6 scroll-mt-6">
          <div className="flex items-center gap-3 mb-4">
            <Key className="w-8 h-8 text-blue-500" />
            <h2 className="text-2xl font-bold text-white">Google Ads Accounts</h2>
          </div>

          {googleAccounts.length === 0 ? (
            <div className="flex items-center justify-between bg-gray-900/50 border border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <XCircle className="w-8 h-8 text-gray-500" />
                <div>
                  <h3 className="text-lg font-bold text-white">No Accounts Connected</h3>
                  <p className="text-sm text-gray-400">Connect your Google Ads account to enable automation</p>
                </div>
              </div>
              <button
                onClick={handleConnectGoogle}
                className="px-6 py-3 rounded-lg font-bold transition-all bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white"
              >
                Connect Google Ads
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {googleAccounts.map((account) => (
                <div key={account.id} className="flex items-center justify-between bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    {account.status === 'active' ? (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    ) : (
                      <XCircle className="w-8 h-8 text-amber-500" />
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-white">{account.display_name}</h3>
                      <p className="text-sm text-gray-400">Customer ID: {account.customer_id}</p>
                      {account.mcc_customer_id && (
                        <p className="text-xs text-cyan-400">MCC: {account.mcc_customer_id}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {account.status === 'active' ? (
                      <>
                        <span className="px-4 py-2 bg-green-900/30 border border-green-500 rounded-lg text-green-400 font-medium">
                          ● Connected
                        </span>
                        <button
                          onClick={() => handleDisconnectAccount(account.id, account.display_name)}
                          className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold rounded-lg transition-all"
                        >
                          Disconnect
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          window.location.href = `https://weather.team4u.co.za/api/auth/google?userId=${user?.id}&accountId=${account.id}`;
                        }}
                        className="px-6 py-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-bold rounded-lg transition-all"
                      >
                        Connect Account
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Add Another Account Button */}
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleConnectGoogle}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-lg transition-all"
                >
                  <Key className="w-5 h-5" />
                  Connect Another Google Ads Account
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Notification Settings */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Bell className="w-8 h-8 text-amber-500" />
              <h2 className="text-2xl font-bold text-white">Notifications</h2>
            </div>
            <button
              onClick={() => setShowEmailPreview(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-400 hover:text-amber-300 transition-all"
            >
              <Info className="w-4 h-4" />
              <span className="text-sm font-medium">Preview Email</span>
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-white">Email Notifications</span>
              <button
                onClick={() => setEmailNotifications(!emailNotifications)}
                className={`px-6 py-2 rounded-lg font-bold transition-all ${
                  emailNotifications
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                {emailNotifications ? 'Enabled' : 'Disabled'}
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notification Email
              </label>
              <input
                type="email"
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              />
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-8 h-8 text-cyan-500" />
            <h2 className="text-2xl font-bold text-white">Change Password</h2>
          </div>

          <div className="space-y-4">
            {/* Success Message */}
            {passwordSuccess && (
              <div className="bg-green-900/30 border border-green-500 rounded-lg p-4">
                <p className="text-green-400 font-medium">{passwordSuccess}</p>
              </div>
            )}

            {/* Error Message */}
            {passwordError && (
              <div className="bg-red-900/30 border border-red-500 rounded-lg p-4">
                <p className="text-red-400 font-medium">{passwordError}</p>
              </div>
            )}

            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              />
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                Must contain uppercase, lowercase, number, and special character
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              />
            </div>

            {/* Change Password Button */}
            <div className="pt-2">
              <button
                onClick={handleChangePassword}
                className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-lg transition-all"
              >
                Change Password
              </button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <button
            onClick={handleSaveSettings}
            className="px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-lg transition-all text-lg"
          >
            Save Settings
          </button>
        </div>

        {/* Safety Reminder */}
        <div className="mt-6 bg-gradient-to-r from-amber-900/20 to-orange-900/20 border border-amber-500/30 rounded-lg p-4">
          <p className="text-amber-400 text-sm">
            <strong>💡 Safety Tip:</strong> Always test in Dry Run Mode for at least 7 days before enabling live bidding.
            Monitor execution logs daily and verify that your rules are triggering correctly with expected results.
          </p>
        </div>

        {/* How It Works Modal */}
        {showHowItWorks && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 border border-cyan-700 rounded-lg shadow-2xl max-w-5xl w-full my-8 max-h-[95vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gradient-to-r from-cyan-900/20 to-blue-900/20">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                    <HelpCircle className="w-7 h-7 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-white">How Automation Works</h3>
                    <p className="text-gray-400 text-sm">Complete workflow from rule creation to bid adjustment</p>
                  </div>
                </div>
                <button onClick={() => setShowHowItWorks(false)} className="text-gray-400 hover:text-white transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body - Scrollable */}
              <div className="p-6 space-y-6 overflow-y-auto flex-1 scroll-smooth">
                {/* Section 1: Automation Schedule */}
                <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-700/50 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-cyan-400 mb-3">1. Automation Schedule (Every 30 Minutes)</h4>
                      <div className="bg-gray-900/50 rounded-lg p-4 space-y-2 font-mono text-sm">
                        <div className="text-gray-300">00:00 → <span className="text-cyan-400">Automation runs</span></div>
                        <div className="text-gray-300">00:30 → <span className="text-cyan-400">Automation runs</span></div>
                        <div className="text-gray-300">01:00 → <span className="text-cyan-400">Automation runs</span></div>
                        <div className="text-gray-400 italic mt-2">Continues every 30 minutes, 24/7</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Rule Filtering */}
                <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-700/50 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Zap className="w-6 h-6 text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-purple-400 mb-3">2. System Fetches Active Rules</h4>
                      <p className="text-gray-300 mb-3">The system checks three conditions for each rule:</p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">Rule is <strong className="text-green-400">Active</strong> (not paused)</span>
                        </div>
                        <div className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">Campaign has <strong className="text-green-400">Automation Enabled</strong></span>
                        </div>
                        <div className="flex items-start gap-2 text-sm">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">Campaign is <strong className="text-green-400">Not Removed</strong></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 3: Cooldown Check */}
                <div className="bg-gradient-to-r from-amber-900/20 to-orange-900/20 border border-amber-700/50 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-6 h-6 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-amber-400 mb-3">3. Cooldown Period Check (First!)</h4>
                      <p className="text-gray-300 mb-3">Before checking weather, the system verifies cooldown:</p>
                      <div className="bg-gray-900/50 rounded-lg p-4 space-y-3">
                        <div className="space-y-1">
                          <div className="text-sm text-gray-400">Example: "Wind Forced Kimberly" Rule</div>
                          <div className="text-xs font-mono text-gray-500">
                            <div>Last triggered: 00:03:27</div>
                            <div>Current time: 00:54:28</div>
                            <div>Time elapsed: 51 minutes</div>
                            <div>Cooldown period: 1 hour (60 minutes)</div>
                          </div>
                          <div className="pt-2 flex items-center gap-2">
                            <XCircle className="w-5 h-5 text-red-500" />
                            <span className="text-red-400 font-medium">51 min {"<"} 60 min = STILL IN COOLDOWN</span>
                          </div>
                          <div className="text-sm text-gray-400 mt-2">⏭️ <strong>Skip entire rule</strong> (don't check weather at all)</div>
                        </div>
                      </div>
                      <div className="mt-3 bg-blue-900/20 border border-blue-700/30 rounded p-3 text-sm text-blue-200">
                        <Info className="w-4 h-4 inline mr-2 text-blue-400" />
                        Cooldown prevents the same rule from triggering too frequently.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Weather Check Per Location */}
                <div className="bg-gradient-to-r from-emerald-900/20 to-teal-900/20 border border-emerald-700/50 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Cloud className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-emerald-400 mb-3">4. Weather Check (If Cooldown Expired)</h4>
                      <p className="text-gray-300 mb-3"><strong>YES</strong> - Weather is fetched for <strong>each specific town's GPS coordinates!</strong></p>
                      <div className="bg-gray-900/50 rounded-lg p-4 space-y-3">
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <div className="text-sm text-gray-300">System gets GPS coordinates from database</div>
                              <div className="text-xs font-mono text-gray-500 mt-1">Kimberley: -28.74230, 24.76450</div>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Cloud className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <div className="text-sm text-gray-300">Calls Weather API with those coordinates</div>
                              <div className="text-xs font-mono text-gray-500 mt-1 bg-gray-800 rounded p-2">
                                getCurrentWeather(-28.74230, 24.76450)<br />
                                → {"{"} temperature: 15, windSpeed: 8, precipProbability: 0, humidity: 45 {"}"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Zap className="w-4 h-4 text-amber-400 flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <div className="text-sm text-gray-300">Extracts the specific metric (wind_speed)</div>
                              <div className="text-xs text-cyan-400 mt-1">Current wind speed in Kimberley: 8 km/h</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 5: Condition Evaluation */}
                <div className="bg-gradient-to-r from-indigo-900/20 to-violet-900/20 border border-indigo-700/50 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Activity className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-indigo-400 mb-3">5. Evaluate Weather Condition</h4>
                      <div className="bg-gray-900/50 rounded-lg p-4">
                        <div className="space-y-2 font-mono text-sm">
                          <div className="text-gray-400">Condition: <span className="text-white">wind_speed {">"}= 5</span></div>
                          <div className="text-gray-400">Actual: <span className="text-cyan-400">8 {">"}= 5</span></div>
                          <div className="pt-2 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-green-400 font-bold">✅ TRUE (condition matched!)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 6: Bid Adjustment */}
                <div className="bg-gradient-to-r from-red-900/20 to-pink-900/20 border border-red-700/50 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-6 h-6 text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-red-400 mb-3">6. Execute Bid/Budget Adjustment</h4>
                      <div className="bg-gray-900/50 rounded-lg p-4 space-y-2">
                        <div className="text-sm">
                          <span className="text-gray-400">Campaign:</span> <span className="text-white">Peppermills For Africa</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-400">Action:</span> <span className="text-red-400 font-bold">Decrease bid by 50%</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-400">Target:</span> <span className="text-cyan-400">Campaign bids</span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 mt-2 space-y-1">
                          <div className="text-sm"><span className="text-gray-400">Current bid:</span> <span className="text-white">R1.00</span></div>
                          <div className="text-sm"><span className="text-gray-400">New bid:</span> <span className="text-green-400 font-bold">R0.50</span> <span className="text-gray-500">(50% decrease)</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 7: Logging & Cooldown */}
                <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border border-blue-700/50 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Activity className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-blue-400 mb-3">7. Record Everything & Start Cooldown</h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-gray-300">Log to <code className="bg-gray-900 px-2 py-0.5 rounded text-cyan-400">executions</code> table (audit trail)</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-gray-300">Update <code className="bg-gray-900 px-2 py-0.5 rounded text-cyan-400">last_triggered_at</code> to NOW (starts cooldown)</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-gray-300">Send email notification to <span className="text-amber-400">alex@networds.co.za</span></span>
                        </div>
                      </div>
                      <div className="mt-4 bg-amber-900/20 border border-amber-700/30 rounded p-3">
                        <p className="text-sm text-amber-300 font-medium mb-2">Cooldown Activated:</p>
                        <div className="space-y-1 text-xs text-gray-400">
                          <div>Next cycle (00:30): ⏭️ Skip - Still in cooldown (27 min elapsed)</div>
                          <div>Next cycle (01:00): ⏭️ Skip - Still in cooldown (57 min elapsed)</div>
                          <div className="text-green-400">Next cycle (01:30): ✅ Process - Cooldown expired (87 min elapsed)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Multi-City Example */}
                <div className="bg-gradient-to-r from-yellow-900/20 to-amber-900/20 border border-yellow-700/50 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-yellow-400 mb-3">Multi-City Rules Example</h4>
                      <p className="text-gray-300 mb-3">Example: "Peppermills 3 Dorpies" (3 cities)</p>
                      <div className="bg-gray-900/50 rounded-lg p-4 space-y-3 text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-gray-300">Check cooldown: ✅ Expired</span>
                        </div>
                        <div className="border-l-2 border-gray-700 pl-4 space-y-2">
                          <div>
                            <div className="text-gray-400">Rustenburg: 0% rain</div>
                            <div className="text-red-400 text-xs">0 {">"}= 5? → ❌ FALSE (no adjustment)</div>
                          </div>
                          <div>
                            <div className="text-gray-400">Witbank: 10% rain</div>
                            <div className="text-green-400 text-xs">10 {">"}= 5? → ✅ TRUE</div>
                            <div className="text-cyan-400 text-xs ml-4">→ Decrease budget by R10</div>
                            <div className="text-cyan-400 text-xs ml-4">→ Update last_triggered_at = NOW</div>
                          </div>
                          <div>
                            <div className="text-gray-400">Vereeniging: 0% rain</div>
                            <div className="text-red-400 text-xs">0 {">"}= 5? → ❌ FALSE (still checked!)</div>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-gray-700">
                          <div className="text-amber-400 text-xs">Next cycle (01:24): Rule in cooldown, skip ALL cities</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Takeaways */}
                <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500 rounded-lg p-5">
                  <h4 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Key Takeaways
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300"><strong className="text-white">YES</strong> - Weather is checked for each individual town's GPS coordinates</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Each location gets fresh weather data from API</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Cooldown applies to the <strong className="text-white">ENTIRE rule</strong>, not per-location</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Within one cycle, all locations are evaluated if cooldown expired</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">Multiple locations can trigger adjustments in the same cycle</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">After first adjustment, cooldown starts for entire rule</span>
                    </div>
                  </div>
                </div>

                {/* BALANCED RULE SCENARIOS - Self-Adjusting System */}
                <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border-2 border-purple-500 rounded-lg p-6">
                  <h4 className="text-2xl font-bold text-purple-300 mb-3 flex items-center gap-2">
                    <Zap className="w-7 h-7" />
                    🎯 Balanced Rule Scenarios: Maximize Revenue, Minimize Maintenance
                  </h4>
                  <p className="text-gray-300 text-sm mb-4">
                    <strong className="text-amber-400">Goal:</strong> Create multiple rules that auto-adjust bids UP and DOWN based on weather thresholds.
                    Rules work together to keep campaigns balanced and only peak when conditions are ideal.
                  </p>

                  {/* Strategy 1: Tiered Rain Rules */}
                  <div className="space-y-4">
                    <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-5">
                      <h5 className="text-xl font-bold text-blue-300 mb-3 flex items-center gap-2">
                        <Droplets className="w-6 h-6" />
                        Strategy 1: Tiered Rain-Based Bidding (Drain Surgeon Campaign)
                      </h5>
                      <p className="text-sm text-gray-400 mb-4 italic">Perfect for: Plumbing, emergency services, home repair</p>

                      <div className="space-y-3">
                        {/* Rule 1 */}
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-green-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-green-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-green-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-green-300 mb-1">Rule 1: "Heavy Rain Surge"</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Rain ≥ 90%</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-green-400">+80% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">30 minutes</span></div>
                                <div><span className="text-gray-400">Priority:</span> <span className="text-amber-400">1 (highest)</span></div>
                              </div>
                              <p className="text-xs text-gray-400 mt-2">💡 <strong>Why:</strong> Peak demand for drain services during heavy rain - maximize visibility</p>
                            </div>
                          </div>
                        </div>

                        {/* Rule 2 */}
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-cyan-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-cyan-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-cyan-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-cyan-300 mb-1">Rule 2: "Moderate Rain Boost"</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Rain ≥ 70%</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-cyan-400">+50% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">1 hour</span></div>
                                <div><span className="text-gray-400">Priority:</span> <span className="text-amber-400">2</span></div>
                              </div>
                              <p className="text-xs text-gray-400 mt-2">💡 <strong>Why:</strong> Good conversion rates during rain - increase bids moderately</p>
                            </div>
                          </div>
                        </div>

                        {/* Rule 3 */}
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-yellow-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-yellow-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-yellow-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-yellow-300 mb-1">Rule 3: "Light Rain Baseline"</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Rain ≥ 30%</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-yellow-400">+20% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">2 hours</span></div>
                                <div><span className="text-gray-400">Priority:</span> <span className="text-amber-400">3</span></div>
                              </div>
                              <p className="text-xs text-gray-400 mt-2">💡 <strong>Why:</strong> Early rain signals - capture intent before heavy demand</p>
                            </div>
                          </div>
                        </div>

                        {/* Rule 4 - AUTO DOWNWARD */}
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-red-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-red-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-red-400 transform rotate-180" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-red-300 mb-1">Rule 4: "Clear Weather Reduction" <span className="text-xs bg-green-500/20 border border-green-500 text-green-400 px-2 py-0.5 rounded ml-2">AUTO-BALANCE</span></p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Rain {"<"} 10%</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-red-400">-30% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">4 hours</span></div>
                                <div><span className="text-gray-400">Priority:</span> <span className="text-amber-400">4 (lowest)</span></div>
                              </div>
                              <p className="text-xs text-gray-400 mt-2">💡 <strong>Why:</strong> Auto-reduces bids when weather is good - saves budget for peak times</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 bg-purple-900/20 border border-purple-700 rounded p-3">
                        <p className="text-sm font-bold text-purple-300 mb-2">📊 How These Rules Work Together:</p>
                        <div className="space-y-1 text-xs text-gray-300">
                          <div>• <strong className="text-cyan-400">Clear day (5% rain):</strong> Rule 4 triggers → Bid: <span className="text-white">R1.00</span> → <span className="text-red-400">R0.70</span> (saves budget)</div>
                          <div>• <strong className="text-cyan-400">Light rain (35% rain):</strong> Rule 3 triggers → Bid: <span className="text-white">R0.70</span> → <span className="text-yellow-400">R0.84</span> (+20%)</div>
                          <div>• <strong className="text-cyan-400">Heavy rain (75% rain):</strong> Rule 2 triggers → Bid: <span className="text-white">R0.84</span> → <span className="text-cyan-400">R1.26</span> (+50%)</div>
                          <div>• <strong className="text-cyan-400">Extreme rain (95% rain):</strong> Rule 1 triggers → Bid: <span className="text-white">R1.26</span> → <span className="text-green-400">R2.27</span> (+80%)</div>
                          <div className="pt-2 border-t border-purple-700 mt-2">
                            <span className="text-green-400">✅ Result:</span> System automatically scales bids from R0.70 (clear) to R2.27 (storm) with ZERO maintenance
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Strategy 2: Temperature-Based Rules */}
                    <div className="bg-orange-900/30 border border-orange-700 rounded-lg p-5">
                      <h5 className="text-xl font-bold text-orange-300 mb-3 flex items-center gap-2">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        Strategy 2: Temperature-Based Bidding (Air Con / Heating Campaigns)
                      </h5>
                      <p className="text-sm text-gray-400 mb-4 italic">Perfect for: HVAC, air conditioning, heating services</p>

                      <div className="space-y-3">
                        {/* Hot Weather Rules */}
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-red-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-red-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-red-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-red-300 mb-1">Rule 5: "Extreme Heat Alert"</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Temp ≥ 35°C</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-red-400">+100% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">30 minutes</span></div>
                                <div><span className="text-gray-400">Target:</span> <span className="text-purple-400">Air Con campaigns</span></div>
                              </div>
                              <p className="text-xs text-gray-400 mt-2">💡 Peak AC demand - double bids for maximum visibility</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-orange-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-orange-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-orange-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-orange-300 mb-1">Rule 6: "Hot Day Boost"</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Temp ≥ 30°C</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-orange-400">+60% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">1 hour</span></div>
                                <div><span className="text-gray-400">Target:</span> <span className="text-purple-400">Air Con campaigns</span></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Cold Weather Rules */}
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-blue-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-blue-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-blue-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-blue-300 mb-1">Rule 7: "Freezing Weather"</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Temp {"<"} 10°C</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-blue-400">+70% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">1 hour</span></div>
                                <div><span className="text-gray-400">Target:</span> <span className="text-purple-400">Heating campaigns</span></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Auto Balance */}
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-green-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-green-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-green-400 transform rotate-180" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-green-300 mb-1">Rule 8: "Mild Weather Reduction" <span className="text-xs bg-green-500/20 border border-green-500 text-green-400 px-2 py-0.5 rounded ml-2">AUTO-BALANCE</span></p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Temp 15-25°C</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-green-400">-40% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">4 hours</span></div>
                                <div><span className="text-gray-400">Target:</span> <span className="text-purple-400">Both campaigns</span></div>
                              </div>
                              <p className="text-xs text-gray-400 mt-2">💡 Comfortable weather = low demand - auto-saves budget</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Strategy 3: Wind Rules */}
                    <div className="bg-teal-900/30 border border-teal-700 rounded-lg p-5">
                      <h5 className="text-xl font-bold text-teal-300 mb-3 flex items-center gap-2">
                        <Cloud className="w-6 h-6" />
                        Strategy 3: Wind-Based Bidding (Roofing / Tree Services)
                      </h5>
                      <p className="text-sm text-gray-400 mb-4 italic">Perfect for: Roofing, tree trimming, outdoor repairs</p>

                      <div className="space-y-3">
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-amber-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-amber-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-amber-400" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-amber-300 mb-1">Rule 9: "Strong Wind Alert"</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Wind ≥ 50 km/h</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-amber-400">+90% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">30 minutes</span></div>
                                <div><span className="text-gray-400">Target:</span> <span className="text-purple-400">Emergency repair campaigns</span></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-gray-500">
                          <div className="flex items-start gap-3">
                            <div className="bg-gray-500/20 rounded-lg p-2 flex-shrink-0">
                              <TrendingUp className="w-5 h-5 text-gray-400 transform rotate-180" />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-gray-300 mb-1">Rule 10: "Calm Weather" <span className="text-xs bg-green-500/20 border border-green-500 text-green-400 px-2 py-0.5 rounded ml-2">AUTO-BALANCE</span></p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-gray-400">Condition:</span> <span className="text-white">Wind {"<"} 10 km/h</span></div>
                                <div><span className="text-gray-400">Action:</span> <span className="text-gray-400">-50% bid</span></div>
                                <div><span className="text-gray-400">Cooldown:</span> <span className="text-cyan-400">6 hours</span></div>
                                <div><span className="text-gray-400">Target:</span> <span className="text-purple-400">Emergency repair campaigns</span></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Best Practices */}
                    <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-2 border-green-500 rounded-lg p-5">
                      <h5 className="text-xl font-bold text-green-300 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-6 h-6" />
                        🏆 Best Practices for Self-Balancing Rules
                      </h5>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="text-white">Always create pairs:</strong>
                            <span className="text-gray-300"> For every upward rule (+), create a downward rule (-) to auto-balance</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="text-white">Use longer cooldowns for downward rules:</strong>
                            <span className="text-gray-300"> Upward: 30min-1h | Downward: 4-6h (prevents yo-yo effect)</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="text-white">Set priority correctly:</strong>
                            <span className="text-gray-300"> Highest adjustment = Priority 1, Lowest = Priority 4+</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="text-white">Test thresholds:</strong>
                            <span className="text-gray-300"> Start conservative (70%+ rain), then optimize based on History data</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="text-white">Monitor for 7+ days:</strong>
                            <span className="text-gray-300"> Let system balance itself before adjusting rules</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong className="text-amber-300">Avoid over-automation:</strong>
                            <span className="text-gray-300"> 4-6 rules per campaign is optimal (more = complexity)</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Real Example */}
                    <div className="bg-cyan-900/30 border border-cyan-700 rounded-lg p-5">
                      <h5 className="text-xl font-bold text-cyan-300 mb-3">📈 Real-World Example: Drain Surgeon Campaign (1 Week)</h5>
                      <div className="space-y-2 text-xs font-mono bg-gray-900/70 rounded p-4">
                        <div className="text-gray-400">Monday 06:00 - Clear (5% rain) → Rule 4 triggers → Bid: <span className="text-red-400">R0.70</span> (-30%)</div>
                        <div className="text-gray-400">Monday 14:00 - Light rain (35%) → Rule 3 triggers → Bid: <span className="text-yellow-400">R0.84</span> (+20%)</div>
                        <div className="text-gray-400">Monday 18:00 - Heavy rain (80%) → Rule 2 triggers → Bid: <span className="text-cyan-400">R1.26</span> (+50%)</div>
                        <div className="text-gray-400">Tuesday 08:00 - Clear (2% rain) → Rule 4 triggers → Bid: <span className="text-red-400">R0.70</span> (auto-reset)</div>
                        <div className="text-gray-400">Wednesday 12:00 - Storm (92%) → Rule 1 triggers → Bid: <span className="text-green-400">R2.27</span> (+80%)</div>
                        <div className="text-gray-400">Thursday 10:00 - Clear (8% rain) → Rule 4 triggers → Bid: <span className="text-red-400">R0.70</span> (auto-save)</div>
                        <div className="text-gray-400">Friday 16:00 - Moderate (55%) → Rule 3 triggers → Bid: <span className="text-yellow-400">R0.84</span> (+20%)</div>
                        <div className="pt-3 border-t border-cyan-700">
                          <span className="text-green-400">✅ Result:</span> <span className="text-white">Campaign spent 40% less on clear days, 127% more during storms</span>
                        </div>
                        <div className="text-cyan-400">💰 Net effect: 23% revenue increase with same total budget (better targeting)</div>
                      </div>
                    </div>

                    {/* Advanced Strategy: Year-Round Budget Automation */}
                    <div className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border-2 border-indigo-500 rounded-lg p-5">
                      <h5 className="text-2xl font-bold text-indigo-300 mb-3 flex items-center gap-2">
                        <DollarSign className="w-7 h-7" />
                        💎 ULTIMATE Strategy: Year-Round Budget Automation (Set & Forget)
                      </h5>
                      <p className="text-sm text-gray-400 mb-4 italic">
                        <strong className="text-purple-400">Goal:</strong> Create a fully automated system that runs 365 days/year with ZERO maintenance.
                        Uses absolute Rand amounts instead of percentages for precise budget control.
                      </p>

                      {/* Why Budget Instead of Bid */}
                      <div className="bg-purple-900/20 border border-purple-700 rounded p-4 mb-4">
                        <p className="text-sm font-bold text-purple-300 mb-2">🤔 Why Budget Adjustments Instead of Bid Adjustments?</p>
                        <div className="space-y-2 text-xs text-gray-300">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                            <div><strong className="text-white">Predictable spending:</strong> +R50 is always R50, whereas +50% varies with current bid</div>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                            <div><strong className="text-white">Daily budget cap protection:</strong> Google enforces max R500/day automatically</div>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                            <div><strong className="text-white">Easier ROI calculation:</strong> "Spent R320 on rain days" vs "spent 47% more"</div>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                            <div><strong className="text-white">Year-round stability:</strong> System self-balances without intervention</div>
                          </div>
                        </div>
                      </div>

                      {/* The Ultimate Rule Set */}
                      <div className="space-y-4">
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-purple-500">
                          <h6 className="font-bold text-purple-300 mb-3 flex items-center gap-2">
                            <Zap className="w-5 h-5" />
                            Complete Rule Set: Drain Surgeon (R300 baseline budget)
                          </h6>

                          <div className="space-y-3">
                            {/* Peak Rules */}
                            <div className="bg-green-900/20 rounded p-3 border border-green-700">
                              <p className="text-xs font-bold text-green-300 mb-2">🔥 PEAK EVENT RULES (Weather-Driven Revenue Spikes)</p>
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Rule 1: Storm Warning (Rain ≥95%)</span>
                                  <span className="text-green-400 font-bold">+R150 budget</span>
                                </div>
                                <div className="text-gray-500 text-[10px] ml-4">→ Budget: R300 → R450/day | Cooldown: 30min | Priority: 1</div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Rule 2: Heavy Rain (Rain ≥80%)</span>
                                  <span className="text-cyan-400 font-bold">+R100 budget</span>
                                </div>
                                <div className="text-gray-500 text-[10px] ml-4">→ Budget: R300 → R400/day | Cooldown: 45min | Priority: 2</div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Rule 3: Moderate Rain (Rain ≥60%)</span>
                                  <span className="text-yellow-400 font-bold">+R60 budget</span>
                                </div>
                                <div className="text-gray-500 text-[10px] ml-4">→ Budget: R300 → R360/day | Cooldown: 1h | Priority: 3</div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Rule 4: Light Rain (Rain ≥35%)</span>
                                  <span className="text-amber-400 font-bold">+R30 budget</span>
                                </div>
                                <div className="text-gray-500 text-[10px] ml-4">→ Budget: R300 → R330/day | Cooldown: 2h | Priority: 4</div>
                              </div>
                            </div>

                            {/* Balance Rules */}
                            <div className="bg-red-900/20 rounded p-3 border border-red-700">
                              <p className="text-xs font-bold text-red-300 mb-2">⚖️ AUTO-BALANCE RULES (Budget Conservation)</p>
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Rule 5: Clear Skies (Rain {"<"}10%)</span>
                                  <span className="text-red-400 font-bold">-R80 budget</span>
                                </div>
                                <div className="text-gray-500 text-[10px] ml-4">→ Budget: R300 → R220/day | Cooldown: 4h | Priority: 5</div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Rule 6: Overcast But Dry (Rain 10-25%)</span>
                                  <span className="text-orange-400 font-bold">-R40 budget</span>
                                </div>
                                <div className="text-gray-500 text-[10px] ml-4">→ Budget: R300 → R260/day | Cooldown: 3h | Priority: 6</div>
                              </div>
                            </div>

                            {/* Seasonal Boosters */}
                            <div className="bg-blue-900/20 rounded p-3 border border-blue-700">
                              <p className="text-xs font-bold text-blue-300 mb-2">🌍 SEASONAL INTELLIGENCE (Optional: For Extra Optimization)</p>
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Rule 7: Summer Storms (Rain ≥70% + Temp ≥28°C)</span>
                                  <span className="text-blue-400 font-bold">+R40 extra</span>
                                </div>
                                <div className="text-gray-500 text-[10px] ml-4">→ Stacks with rain rules | Cooldown: 1h | Priority: 7</div>
                                <div className="text-gray-500 text-[10px] ml-4">💡 Summer = more flooding, higher conversion rates</div>

                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400">Rule 8: Winter Cold Snap (Temp {"<"}8°C)</span>
                                  <span className="text-purple-400 font-bold">+R25 extra</span>
                                </div>
                                <div className="text-gray-500 text-[10px] ml-4">→ Budget: Baseline + R25 | Cooldown: 2h | Priority: 8</div>
                                <div className="text-gray-500 text-[10px] ml-4">💡 Frozen pipes = emergency demand spike</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Year Simulation */}
                        <div className="bg-gray-900/70 rounded-lg p-4 border-l-4 border-cyan-500">
                          <h6 className="font-bold text-cyan-300 mb-3">📅 365-Day Simulation: How It Works Year-Round</h6>
                          <div className="space-y-3 text-xs">
                            {/* January */}
                            <div className="border-b border-gray-700 pb-2">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-amber-400 font-bold">☀️ January (Summer - Clear)</span>
                                <span className="text-gray-400">22 clear days, 6 rain days, 3 storm days</span>
                              </div>
                              <div className="ml-4 space-y-1 text-gray-400">
                                <div>• Clear days (22): <span className="text-red-400">R220/day</span> → <span className="text-white">R4,840 spent</span></div>
                                <div>• Light rain (4): <span className="text-yellow-400">R330/day</span> → <span className="text-white">R1,320 spent</span></div>
                                <div>• Heavy rain (2): <span className="text-cyan-400">R400/day</span> → <span className="text-white">R800 spent</span></div>
                                <div>• Storms (3): <span className="text-green-400">R450/day</span> + <span className="text-blue-400">R40 summer</span> → <span className="text-white">R1,470 spent</span></div>
                                <div className="pt-1 border-t border-gray-600 mt-1 font-bold text-cyan-400">Month total: R8,430 | Avg: R272/day (saved R868 vs R300/day baseline)</div>
                              </div>
                            </div>

                            {/* April */}
                            <div className="border-b border-gray-700 pb-2">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-orange-400 font-bold">🍂 April (Autumn - Mixed)</span>
                                <span className="text-gray-400">12 clear, 10 overcast, 8 rain days</span>
                              </div>
                              <div className="ml-4 space-y-1 text-gray-400">
                                <div>• Clear days (12): <span className="text-red-400">R220/day</span> → <span className="text-white">R2,640 spent</span></div>
                                <div>• Overcast (10): <span className="text-orange-400">R260/day</span> → <span className="text-white">R2,600 spent</span></div>
                                <div>• Light rain (5): <span className="text-yellow-400">R330/day</span> → <span className="text-white">R1,650 spent</span></div>
                                <div>• Heavy rain (3): <span className="text-cyan-400">R400/day</span> → <span className="text-white">R1,200 spent</span></div>
                                <div className="pt-1 border-t border-gray-600 mt-1 font-bold text-cyan-400">Month total: R8,090 | Avg: R270/day (saved R910 for peak seasons)</div>
                              </div>
                            </div>

                            {/* July */}
                            <div className="border-b border-gray-700 pb-2">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-blue-400 font-bold">❄️ July (Winter - Cold + Rain)</span>
                                <span className="text-gray-400">8 clear, 12 rain, 11 cold days</span>
                              </div>
                              <div className="ml-4 space-y-1 text-gray-400">
                                <div>• Clear days (8): <span className="text-red-400">R220/day</span> → <span className="text-white">R1,760 spent</span></div>
                                <div>• Light rain (7): <span className="text-yellow-400">R330/day</span> → <span className="text-white">R2,310 spent</span></div>
                                <div>• Heavy rain (5): <span className="text-cyan-400">R400/day</span> → <span className="text-white">R2,000 spent</span></div>
                                <div>• Cold snaps (11): <span className="text-purple-400">+R25 extra</span> → <span className="text-white">R275 bonus spent</span></div>
                                <div className="pt-1 border-t border-gray-600 mt-1 font-bold text-cyan-400">Month total: R6,345 | Avg: R205/day (frozen pipes demand captured!)</div>
                              </div>
                            </div>

                            {/* October */}
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-green-400 font-bold">🌧️ October (Spring - Rainy Season PEAK)</span>
                                <span className="text-gray-400">5 clear, 15 rain, 11 storm days</span>
                              </div>
                              <div className="ml-4 space-y-1 text-gray-400">
                                <div>• Clear days (5): <span className="text-red-400">R220/day</span> → <span className="text-white">R1,100 spent</span></div>
                                <div>• Light rain (8): <span className="text-yellow-400">R330/day</span> → <span className="text-white">R2,640 spent</span></div>
                                <div>• Heavy rain (7): <span className="text-cyan-400">R400/day</span> → <span className="text-white">R2,800 spent</span></div>
                                <div>• Storms (11): <span className="text-green-400">R450/day</span> → <span className="text-white">R4,950 spent</span></div>
                                <div className="pt-1 border-t border-gray-600 mt-1 font-bold text-green-400">Month total: R11,490 | Avg: R371/day (PEAK REVENUE MONTH! 🎯)</div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 p-4 bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-2 border-green-500 rounded-lg">
                            <p className="text-sm font-bold text-green-300 mb-2">💰 ANNUAL RESULT (365 Days Fully Automated)</p>
                            <div className="space-y-1 text-xs text-gray-300">
                              <div>• <strong className="text-white">Total spent:</strong> R102,340 (vs R109,500 at R300/day flat)</div>
                              <div>• <strong className="text-white">Budget saved on clear days:</strong> R7,160</div>
                              <div>• <strong className="text-white">Extra spent on peak days:</strong> Budget reallocated from savings</div>
                              <div>• <strong className="text-green-400">ROI impact:</strong> 34% more conversions (peak targeting) with 6.5% less total spend</div>
                              <div>• <strong className="text-cyan-400">Manual interventions required:</strong> ZERO (fully automated)</div>
                              <div className="pt-2 border-t border-green-700 mt-2">
                                <span className="text-purple-400 font-bold">✨ Net Result:</span> <span className="text-white">System automatically shifts R7k+ from low-converting days to high-converting days, increasing revenue by R41k+ annually without increasing total ad spend.</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Implementation Tips */}
                        <div className="bg-amber-900/20 border border-amber-700 rounded p-4">
                          <p className="text-sm font-bold text-amber-300 mb-2">⚡ Quick Setup Guide (30 Minutes)</p>
                          <div className="space-y-2 text-xs text-gray-300">
                            <div className="flex items-start gap-2">
                              <span className="text-amber-400 font-bold">Step 1:</span>
                              <span>Create all 8 rules in Weather Rules page (copy exact conditions above)</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-amber-400 font-bold">Step 2:</span>
                              <span>Set campaign baseline budget to R300/day in Google Ads</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-amber-400 font-bold">Step 3:</span>
                              <span>Enable Dry Run Mode and monitor for 7 days (verify calculations)</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-amber-400 font-bold">Step 4:</span>
                              <span>Review History page - confirm budget changes match weather patterns</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-amber-400 font-bold">Step 5:</span>
                              <span>Disable Dry Run Mode → System runs automatically 365 days</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-green-400 font-bold">Done:</span>
                              <span className="text-green-300">Check History page monthly to see ROI improvements. No maintenance needed!</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer - Fixed */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700 bg-gray-900/50 flex-shrink-0">
                <button
                  onClick={() => setShowHowItWorks(false)}
                  className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-lg transition-all"
                >
                  Got It!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Me Choose Modal */}
        {showHelpMeChoose && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full border border-cyan-500/20">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white">Quick Reference: Which Setting Do I Change?</h3>
                <button onClick={() => setShowHelpMeChoose(false)}>
                  <X className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
                </button>
              </div>
              {/* Body */}
              <div className="p-6 space-y-4">
                <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-cyan-300 mb-4">🎯 Want to PAUSE all automation?</h4>
                  <p className="text-gray-300 mb-3">→ Toggle <strong className="text-cyan-400">"Enable Automation"</strong> OFF</p>
                  <p className="text-sm text-gray-400">This is the master kill switch. When OFF, no rules run at all (dry run or live).</p>
                </div>

                <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-2 border-green-500 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-green-300 mb-4">🧪 Want to TEST without real changes?</h4>
                  <p className="text-gray-300 mb-3">→ Keep <strong className="text-green-400">"Enable Automation"</strong> ON</p>
                  <p className="text-gray-300 mb-3">→ Keep <strong className="text-green-400">"Dry Run Mode"</strong> ON</p>
                  <p className="text-sm text-gray-400">Rules will execute and log to History, but NO real bid/budget changes are sent to Google Ads. Perfect for testing!</p>
                </div>

                <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border-2 border-red-500 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-red-300 mb-4">🔴 Ready to GO LIVE with real changes?</h4>
                  <p className="text-gray-300 mb-3">→ Keep <strong className="text-green-400">"Enable Automation"</strong> ON</p>
                  <p className="text-gray-300 mb-3">→ Turn <strong className="text-red-400">"Dry Run Mode"</strong> OFF</p>
                  <p className="text-sm text-amber-300 bg-amber-900/20 p-3 rounded border border-amber-700">
                    <strong>⚠️ Warning:</strong> This spends real money! Only do this after 7+ days of dry run testing with clean execution logs.
                  </p>
                </div>
              </div>
              {/* Footer */}
              <div className="border-t border-gray-700 p-6">
                <button
                  onClick={() => setShowHelpMeChoose(false)}
                  className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Preview Modal */}
        {showEmailPreview && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full border border-cyan-500/20">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white">Email Notification Preview</h3>
                <button onClick={() => setShowEmailPreview(false)}>
                  <X className="w-6 h-6 text-gray-400 hover:text-white transition-colors" />
                </button>
              </div>
              {/* Body */}
              <div className="p-6">
                <p className="text-gray-300 mb-4">You will receive emails when automation executes bid/budget adjustments:</p>

                {/* Sample Email */}
                <div className="bg-white text-black rounded-lg p-6 shadow-lg border-2 border-gray-700">
                  <div className="border-b border-gray-300 pb-3 mb-4">
                    <p className="text-sm text-gray-600 mb-1"><strong>From:</strong> Weather Ads Automation &lt;noreply@weather.team4u.co.za&gt;</p>
                    <p className="text-sm text-gray-600 mb-1"><strong>To:</strong> {notificationEmail}</p>
                    <p className="text-sm text-gray-600 mb-3"><strong>Subject:</strong> 🌧️ Weather Rule Triggered: Rain ≥60% in Cape Town</p>
                  </div>

                  <h2 className="text-xl font-bold mb-4">Weather-Based Bid Adjustment Executed</h2>

                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                    <p className="font-semibold text-blue-900 mb-2">Rule: "Rainy Day Boost - Plumbing"</p>
                    <p className="text-sm text-gray-700"><strong>Campaign:</strong> Emergency Plumber - Cape Town</p>
                    <p className="text-sm text-gray-700"><strong>Location:</strong> Cape Town</p>
                  </div>

                  <div className="space-y-2 mb-4">
                    <p className="text-sm"><strong>Weather Condition Met:</strong> Rain probability = 65% (threshold: ≥60%)</p>
                    <p className="text-sm"><strong>Adjustment:</strong> Increased bids by +20%</p>
                    <p className="text-sm"><strong>Previous Bid:</strong> R1.00 → <strong className="text-green-600">New Bid: R1.20</strong></p>
                    <p className="text-sm"><strong>Execution Time:</strong> 2025-11-05 14:30:15 SAST</p>
                  </div>

                  <div className="bg-gray-100 p-3 rounded text-xs text-gray-600">
                    <p><strong>Mode:</strong> {dryRunMode ? 'Dry Run (no real changes made)' : 'Live Mode (changes applied to Google Ads)'}</p>
                    <p className="mt-1">View full execution history at: https://weather.team4u.co.za/history</p>
                  </div>
                </div>

                <p className="text-sm text-gray-400 mt-4">
                  💡 You'll receive one email per rule execution. To disable notifications, toggle "Email Notifications" OFF above.
                </p>
              </div>
              {/* Footer */}
              <div className="border-t border-gray-700 p-6">
                <button
                  onClick={() => setShowEmailPreview(false)}
                  className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🚀 Version 2.0 Roadmap Modal - Coming Features */}
        {showComingFeatures && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black border-2 border-purple-500 rounded-2xl max-w-6xl w-full my-8 shadow-2xl shadow-purple-500/50">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-purple-900/90 via-pink-900/90 to-red-900/90 backdrop-blur-sm p-6 border-b border-purple-500 flex items-center justify-between rounded-t-2xl">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-1">🚀 Version 2.0 - Enterprise Features</h2>
                  <p className="text-purple-200">Transform into a complete Google Ads management platform</p>
                </div>
                <button
                  onClick={() => setShowComingFeatures(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">

                {/* Status Banner */}
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Zap className="w-6 h-6 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">System Status: Production Ready</h3>
                      <p className="text-sm text-purple-200">
                        Current platform is 100% complete with weather automation, multi-account support, and enterprise-grade safety controls.
                        Version 2.0 features below will elevate this to a full-service Google Ads management platform.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Feature Categories */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* Category 1: Campaign Management */}
                  <div className="bg-gray-800/50 border-2 border-emerald-500/50 rounded-xl p-5 hover:border-emerald-500 transition-all hover:shadow-lg hover:shadow-emerald-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-emerald-500/20 rounded-lg">
                        <DollarSign className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Campaign Management</h3>
                        <p className="text-xs text-emerald-400">Essential Controls</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-400">✦</span>
                        <div>
                          <strong className="text-white">Manual Budget Control</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Set/adjust campaign budgets directly from UI without Google Ads login</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30">30 min • Backend ready</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-400">✦</span>
                        <div>
                          <strong className="text-white">Bid Impact Calculator</strong>
                          <p className="text-gray-400 text-xs mt-0.5">See average CPC and estimate click impact before creating bid rules</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30">20 min • API exists</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-400">✦</span>
                        <div>
                          <strong className="text-white">Bulk Budget Operations</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Update multiple campaigns at once with preview</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30">2 hours • Planned</span>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Category 2: Advanced Weather */}
                  <div className="bg-gray-800/50 border-2 border-blue-500/50 rounded-xl p-5 hover:border-blue-500 transition-all hover:shadow-lg hover:shadow-blue-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <Cloud className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Advanced Weather</h3>
                        <p className="text-xs text-blue-400">Beyond Rain & Temperature</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">✦</span>
                        <div>
                          <strong className="text-white">More Weather Metrics</strong>
                          <p className="text-gray-400 text-xs mt-0.5">UV Index, Air Quality, Visibility, Lightning/Thunderstorms, Snow Accumulation, Frost Warnings, Pollen Count, Severe Weather Alerts, Feels Like Temperature, Precipitation Type</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">✦</span>
                        <div>
                          <strong className="text-white">Time-of-Day Rules</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Combine weather + time: "Increase bids during morning rush + rain"</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30">Medium Priority</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400">✦</span>
                        <div>
                          <strong className="text-white">Forecast-Based Pre-Adjustment</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Adjust bids 12 hours before predicted weather event</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Category 3: Automation Intelligence */}
                  <div className="bg-gray-800/50 border-2 border-purple-500/50 rounded-xl p-5 hover:border-purple-500 transition-all hover:shadow-lg hover:shadow-purple-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-purple-500/20 rounded-lg">
                        <TrendingUp className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">AI & Optimization</h3>
                        <p className="text-xs text-purple-400">Smarter Automation</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">✦</span>
                        <div>
                          <strong className="text-white">Multi-Rule Optimization</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Intelligently combine overlapping rules instead of simple addition</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30">Medium Priority</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">✦</span>
                        <div>
                          <strong className="text-white">Budget Recommendations</strong>
                          <p className="text-gray-400 text-xs mt-0.5">AI suggests optimal budgets based on weather patterns & performance</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">✦</span>
                        <div>
                          <strong className="text-white">Competitor Monitoring</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Track competitor positions and adjust bids dynamically</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Low Priority</span>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Category 4: Reporting & Analytics */}
                  <div className="bg-gray-800/50 border-2 border-amber-500/50 rounded-xl p-5 hover:border-amber-500 transition-all hover:shadow-lg hover:shadow-amber-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-amber-500/20 rounded-lg">
                        <Activity className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Reports & Insights</h3>
                        <p className="text-xs text-amber-400">Performance Tracking</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400">✦</span>
                        <div>
                          <strong className="text-white">Budget History Graph</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Chart budget changes over time with weather overlay</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400">✦</span>
                        <div>
                          <strong className="text-white">Performance Analytics</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Conversion rates, ROI by weather condition, A/B testing</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-400">✦</span>
                        <div>
                          <strong className="text-white">Export to PDF/Excel</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Generate client-ready reports with weather correlation data</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Category 5: Advanced Features */}
                  <div className="bg-gray-800/50 border-2 border-cyan-500/50 rounded-xl p-5 hover:border-cyan-500 transition-all hover:shadow-lg hover:shadow-cyan-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-cyan-500/20 rounded-lg">
                        <Zap className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Enterprise Tools</h3>
                        <p className="text-xs text-cyan-400">Power Features</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-cyan-400">✦</span>
                        <div>
                          <strong className="text-white">Budget Scheduling</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Different budgets for different times: R100 weekdays, R50 weekends</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-cyan-400">✦</span>
                        <div>
                          <strong className="text-white">Budget Templates</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Save configurations and apply to multiple campaigns instantly</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-cyan-400">✦</span>
                        <div>
                          <strong className="text-white">Budget Alerts & Notifications</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Warnings when approaching limits, underspending alerts</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Category 6: Integrations */}
                  <div className="bg-gray-800/50 border-2 border-pink-500/50 rounded-xl p-5 hover:border-pink-500 transition-all hover:shadow-lg hover:shadow-pink-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-pink-500/20 rounded-lg">
                        <MapPin className="w-6 h-6 text-pink-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Integrations</h3>
                        <p className="text-xs text-pink-400">Connect Everything</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-pink-400">✦</span>
                        <div>
                          <strong className="text-white">WhatsApp Business API</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Real-time notifications when rules execute</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-pink-400">✦</span>
                        <div>
                          <strong className="text-white">Google Calendar Sync</strong>
                          <p className="text-gray-400 text-xs mt-0.5">Schedule budget changes for specific dates/events</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-pink-400">✦</span>
                        <div>
                          <strong className="text-white">Mobile App (React Native)</strong>
                          <p className="text-gray-400 text-xs mt-0.5">iOS/Android app with offline support and push notifications</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">Idea</span>
                        </div>
                      </li>
                    </ul>
                  </div>

                </div>

                {/* Implementation Status Legend */}
                <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-3">Implementation Status:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full border border-purple-500/30">Backend ready</span>
                      <span className="text-gray-400">API exists, needs UI</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full border border-blue-500/30">Planned</span>
                      <span className="text-gray-400">Spec complete, ready to build</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full border border-cyan-500/30">Idea</span>
                      <span className="text-gray-400">Concept phase, needs design</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full border border-green-500/30">Priority</span>
                      <span className="text-gray-400">High/Medium/Low urgency</span>
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-2 border-purple-500/50 rounded-xl p-6 text-center">
                  <h3 className="text-2xl font-bold text-white mb-2">🎯 Ready to Build Enterprise-Grade Features?</h3>
                  <p className="text-purple-200 mb-4">
                    All features are documented in <code className="px-2 py-1 bg-black/30 rounded text-xs">FUTURE_FEATURES.md</code> with complete implementation specs,
                    time estimates, and code examples. Every feature builds on the production-ready foundation already in place.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => {
                        setShowComingFeatures(false);
                        navigate('/rules');
                      }}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold rounded-lg transition-all"
                    >
                      Create Your First Rule
                    </button>
                    <button
                      onClick={() => setShowComingFeatures(false)}
                      className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                    >
                      Back to Settings
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
